import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
  Button,
  TextField,
  Alert,
} from "@mui/material";

function SectionCard({ title, right, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
          </Box>
          {right}
        </Stack>
        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.25)" }} />
        {children}
      </CardContent>
    </Card>
  );
}

const whiteField = {
  InputLabelProps: { sx: { color: "#fff" } },
  FormHelperTextProps: { sx: { color: "rgba(255,255,255,0.75)" } },
  sx: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
      "&.Mui-focused fieldset": { borderColor: "#fff" },
    },
  },
};

const whiteFieldError = {
  InputLabelProps: { sx: { color: "#ffb4ab" } },
  FormHelperTextProps: { sx: { color: "#ffb4ab" } },
  sx: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "#ffb4ab" },
      "&:hover fieldset": { borderColor: "#ffb4ab" },
      "&.Mui-focused fieldset": { borderColor: "#ffb4ab" },
    },
  },
};

export default function SectionReviews({
  form,
  setNested,
  updateReviewsSection,
  saving,
  dirty,
  onSaved,
}) {
  const { t } = useTranslation();
  const rawRating = form.googleReviews?.rating;
  const ratingFilled = rawRating !== "" && rawRating != null;
  const ratingNum = parseFloat(rawRating);
  const ratingOutOfRange = ratingFilled && (!Number.isFinite(ratingNum) || ratingNum < 1 || ratingNum > 5);

  const linkFilled = !!form.googleReviews?.link?.trim();
  const bothFilled = ratingFilled && !ratingOutOfRange && linkFilled;
  const onlyOneFilled = (ratingFilled && !ratingOutOfRange && !linkFilled) || (!ratingFilled && linkFilled);

  const save = async () => {
    const payload = {
      googleReviews: {
        rating: ratingNum,
        link: form.googleReviews?.link || "",
      },
    };
    const ok = await updateReviewsSection(payload);
    if (ok) onSaved?.();
  };

  return (
    <SectionCard
      title={t("info.reviews.sectionTitle")}
      right={!bothFilled ? null :
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      <Stack spacing={2}>
        <TextField
          label={t("info.reviews.ratingLabel")}
          value={rawRating ?? ""}
          onChange={(e) => setNested("googleReviews", "rating", e.target.value)}
          type="number"
          inputProps={{ min: 1, max: 5, step: 0.1 }}
          fullWidth
          error={ratingOutOfRange}
          helperText={ratingOutOfRange ? t("info.reviews.ratingError") : t("info.reviews.ratingHint")}
          {...(ratingOutOfRange ? whiteFieldError : whiteField)}
        />

        <TextField
          label={t("info.reviews.linkLabel")}
          value={form.googleReviews?.link ?? ""}
          onChange={(e) => setNested("googleReviews", "link", e.target.value)}
          fullWidth
          placeholder="https://maps.google.com/..."
          {...whiteField}
        />

        {onlyOneFilled && (
          <Alert
            severity="warning"
            sx={{
              bgcolor: "rgba(255,255,255,0.08)",
              color: "#fff",
              border: "1px solid rgba(255,165,0,0.5)",
              "& .MuiAlert-icon": { color: "orange" },
            }}
          >
            {t("info.reviews.bothFieldsWarning")}
          </Alert>
        )}

        <Alert
          severity="info"
          sx={{
            bgcolor: "rgba(255,255,255,0.08)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.25)",
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          {t("info.reviews.validityNote")}
        </Alert>
      </Stack>
    </SectionCard>
  );
}
